import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ValidationError, SignupFormState } from '../models/User';
import { AuthentificationApi, ApiUsersRegisterPostRequest, ApiUsersLoginPost200Response, Configuration } from '../api/services';

// Interface étendue pour inclure birth_date et phone
interface ExtendedRegisterRequest extends ApiUsersRegisterPostRequest {
  birth_date?: string;
  phone?: string;
}

export class SignupViewModel {
  private navigate: ReturnType<typeof useNavigate>;
  private authApi: AuthentificationApi;

  constructor(navigate: ReturnType<typeof useNavigate>) {
    this.navigate = navigate;
    this.authApi = new AuthentificationApi();
  }

  // État initial
  getInitialState(): SignupFormState {
    return {
      user: {
        name: '',
        surname: '',
        email: '',
        phone: '',
        birthDate: '',
        password: '',
        confirmPassword: ''
      },
      isLoading: false,
      errors: [],
      success: false,
      message: '',
      showSuccessModal: false
    };
  }

  // Validation côté client
  validateForm(user: User): ValidationError[] {
    const errors: ValidationError[] = [];

    // Validation des champs requis
    if (!user.name.trim()) {
      errors.push({ field: 'name', message: 'Le nom est requis' });
    }

    if (!user.surname.trim()) {
      errors.push({ field: 'surname', message: 'Le prénom est requis' });
    }

    if (!user.email.trim()) {
      errors.push({ field: 'email', message: 'L\'adresse email est requise' });
    } else if (!this.isValidEmail(user.email)) {
      errors.push({ field: 'email', message: 'Format d\'email invalide' });
    }

    if (!user.phone.trim()) {
      errors.push({ field: 'phone', message: 'Le numéro de téléphone est requis' });
    }

    if (!user.birthDate) {
      errors.push({ field: 'birthDate', message: 'La date de naissance est requise' });
    }

    if (!user.password) {
      errors.push({ field: 'password', message: 'Le mot de passe est requis' });
    } else if (user.password.length < 6) {
      errors.push({ field: 'password', message: 'Le mot de passe doit contenir au moins 6 caractères' });
    }

    if (!user.confirmPassword) {
      errors.push({ field: 'confirmPassword', message: 'La confirmation du mot de passe est requise' });
    } else if (user.password !== user.confirmPassword) {
      errors.push({ field: 'confirmPassword', message: 'Les mots de passe ne correspondent pas' });
    }

    return errors;
  }

  // Validation email
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Création d'utilisateur
  async createUser(user: User): Promise<{ success: boolean; message?: string; errors?: string[] }> {
    try {
      // Validation côté client
      const validationErrors = this.validateForm(user);
      if (validationErrors.length > 0) {
        return {
          success: false,
          errors: validationErrors.map(e => e.message)
        };
      }

      // Préparation des données pour l'API
      const registrationData: any = {
        first_name: user.name.trim(),
        last_name: user.surname.trim(),
        email: user.email.trim().toLowerCase(),
        password: user.password!
      };

      // Ajouter birth_date et phone si l'API les accepte
      if (user.birthDate) {
        // S'assurer que le format est YYYY-MM-DD
        const birthDate = new Date(user.birthDate);
        const formattedDate = birthDate.toISOString().split('T')[0]; // Format YYYY-MM-DD
        registrationData.birth_date = formattedDate;
        console.log('Date de naissance formatée:', formattedDate);
      }
      
      if (user.phone.trim()) {
        registrationData.phone = user.phone.trim();
      }

      // Console log de l'objet complet envoyé à l'API
      console.log('=== OBJET ENVOYÉ À L\'API ===');
      console.log('Objet registrationData:', registrationData);
      console.log('JSON.stringify:', JSON.stringify(registrationData, null, 2));
      console.log('================================');

      // Appel API pour créer l'utilisateur
      const response = await this.authApi.apiUsersRegisterPost(registrationData);
      
      console.log('Utilisateur créé avec succès:', response.data);
      console.log('Données envoyées à l\'API:', registrationData);
      
      return {
        success: true,
        message: 'Compte créé avec succès !'
      };

    } catch (error: any) {
      console.error('Erreur lors de la création du compte:', error);
      
      // Gestion des erreurs API
      if (error.response?.data?.message) {
        return {
          success: false,
          message: error.response.data.message
        };
      }

      return {
        success: false,
        message: 'Une erreur est survenue lors de la création du compte'
      };
    }
  }

  // Afficher le modal de succès
  showSuccessModal(): void {
    this.navigate('/signin');
  }

  // Fermer le modal et naviguer
  closeSuccessModal(): void {
    this.navigate('/signin');
  }

  // Mise à jour d'un champ
  updateField(user: User, field: keyof User, value: string): User {
    return {
      ...user,
      [field]: value
    };
  }

  // Vérification si le formulaire est valide
  isFormValid(user: User): boolean {
    const errors = this.validateForm(user);
    return errors.length === 0;
  }
}

// Hook React pour utiliser le ViewModel
export const useSignupViewModel = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<SignupFormState>(new SignupViewModel(navigate).getInitialState());

  const viewModel = new SignupViewModel(navigate);

  const updateField = useCallback((field: keyof User, value: string) => {
    setState(prevState => ({
      ...prevState,
      user: viewModel.updateField(prevState.user, field, value),
      errors: []
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
      errors: [],
      success: false,
      message: ''
    }));

    const result = await viewModel.createUser(state.user);

    if (result.success) {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        success: true,
        message: result.message || 'Compte créé avec succès !',
        showSuccessModal: true
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        errors: result.errors?.map((error: string, index: number) => ({
          field: `field_${index}`,
          message: error
        })) || [],
        message: result.message || 'Erreur lors de la création du compte'
      }));
    }
  }, [state.user]);

  const isFormValid = useCallback(() => {
    return viewModel.isFormValid(state.user);
  }, [state.user]);

  const closeSuccessModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showSuccessModal: false
    }));
    viewModel.closeSuccessModal();
  }, []);

  return {
    state,
    updateField,
    handleSubmit,
    isFormValid,
    closeSuccessModal
  };
}; 